/**
 * Default pricing year for navigation and auto-selection:
 * - Prefer the current calendar year when it exists and is not locked.
 * - If that year is locked, prefer the previous calendar year when it exists and is not locked.
 * - Otherwise prefer the newest unlocked year.
 * - If every year is locked, fall back to the newest year.
 */
export function pickDefaultPricingYear<T extends { year: number; isLocked: boolean }>(
  years: readonly T[]
): T | undefined {
  if (!years.length) {
    return undefined;
  }

  const sortedDesc = [...years].sort((a, b) => b.year - a.year);
  const calendarYear = new Date().getFullYear();

  const recordForYear = (y: number) => sortedDesc.find((py) => py.year === y);

  const currentCalendarRecord = recordForYear(calendarYear);
  if (currentCalendarRecord && !currentCalendarRecord.isLocked) {
    return currentCalendarRecord;
  }

  if (currentCalendarRecord?.isLocked) {
    const previousCalendarRecord = recordForYear(calendarYear - 1);
    if (previousCalendarRecord && !previousCalendarRecord.isLocked) {
      return previousCalendarRecord;
    }
  }

  const newestUnlocked = sortedDesc.find((py) => !py.isLocked);
  if (newestUnlocked) {
    return newestUnlocked;
  }

  return sortedDesc[0];
}
